import { Button, Dialog, DialogBody, DialogFooter, DialogHeader } from '@repo/ui';

import { useRemoveClioCredential } from '../../queries/useClioCredentialsQuery';
import type { PublicCredentialMetadata } from '../../api/clioCredentialsApi';

interface ConfirmRemoveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  credential: PublicCredentialMetadata | null;
}

export function ConfirmRemoveDialog({ isOpen, onClose, credential }: ConfirmRemoveDialogProps) {
  const removeMutation = useRemoveClioCredential();

  const handleConfirm = async () => {
    if (!credential) return;
    try {
      await removeMutation.mutateAsync(credential.credentialId);
      onClose();
    } catch {
      // Toast surfaced via mutation onError
    }
  };

  return (
    <Dialog open={isOpen} onClose={removeMutation.isPending ? () => undefined : onClose} size="sm">
      <DialogHeader>
        <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
          Remove Clio app?
        </h2>
      </DialogHeader>
      <DialogBody>
        <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-200">
          <span className="font-medium">{credential?.label ?? 'This credential'}</span> will be
          removed from your workspace. Its rate-limit state will be deleted. The Clio app itself
          remains in your Clio account and must be removed there separately.
        </p>
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={removeMutation.isPending}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleConfirm}
          loading={removeMutation.isPending}
          disabled={removeMutation.isPending}
          className="!bg-red-600 hover:!bg-red-700 !border-red-600"
        >
          Remove
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
